import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  ListSubheader,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  School as TrainingIcon,
  CheckCircle as CorrectIcon,
  Close as WrongIcon,
  HelpOutline as UnsureIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Phone as VoiceIcon,
  Add as AddIcon,
  Warning as HighRiskIcon,
  Info as ModerateRiskIcon,
  CheckCircleOutline as LowRiskIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Stat card component
const StatCard = ({ icon, label, value, color = 'primary', subtitle }) => (
  <Card variant="outlined">
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ color: `${color}.main`, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" fontWeight={600}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </CardContent>
  </Card>
);

// Status chip component
const StatusChip = ({ status }) => {
  const config = {
    pending: { color: 'default', label: 'Pending' },
    sent: { color: 'info', label: 'Sent' },
    responded: { color: 'warning', label: 'Responded' },
    completed: { color: 'success', label: 'Completed' },
    expired: { color: 'error', label: 'Expired' },
    cancelled: { color: 'default', label: 'Cancelled' },
  };
  const { color, label } = config[status] || { color: 'default', label: status };
  return <Chip size="small" color={color} label={label} />;
};

// Channel icon component
const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case 'sms': return <SmsIcon fontSize="small" />;
    case 'email': return <EmailIcon fontSize="small" />;
    case 'voice': return <VoiceIcon fontSize="small" />;
    default: return null;
  }
};

// Risk level chip
const RiskChip = ({ level }) => {
  const config = {
    high: { color: 'error', icon: <HighRiskIcon fontSize="small" /> },
    moderate: { color: 'warning', icon: <ModerateRiskIcon fontSize="small" /> },
    low: { color: 'success', icon: <LowRiskIcon fontSize="small" /> },
  };
  const { color, icon } = config[level] || { color: 'default', icon: null };
  return (
    <Chip 
      size="small" 
      color={color} 
      icon={icon}
      label={level?.charAt(0).toUpperCase() + level?.slice(1)} 
    />
  );
};

// Response result icon
const ResponseIcon = ({ correct, identified }) => {
  if (correct === true) return <CorrectIcon color="success" />;
  if (correct === false) return <WrongIcon color="error" />;
  if (identified === 'unsure') return <UnsureIcon color="warning" />;
  return null;
};

const Training = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Stats
  const [stats, setStats] = useState(null);

  // Sessions list
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionFilter, setSessionFilter] = useState({ status: '', channel: '' });

  // Scenarios
  const [scenarios, setScenarios] = useState([]);

  // Users for dropdown
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Create session dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [createData, setCreateData] = useState({
    client_id: '',
    scenario_id: '',
    channel: 'sms',
    send_immediately: true
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Load initial data
  useEffect(() => {
    loadStats();
    loadScenarios();
    loadSessions();
  }, []);

  // Load sessions when filter changes
  useEffect(() => {
    loadSessions();
  }, [sessionFilter]);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getTrainingStats();
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadScenarios = async () => {
    try {
      const response = await adminAPI.getTrainingScenarios();
      setScenarios(response.data.scenarios);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    }
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getTrainingSessions({
        status: sessionFilter.status || undefined,
        channel: sessionFilter.channel || undefined,
        limit: 50
      });
      setSessions(response.data.sessions);
      setSessionsTotal(response.data.total);
    } catch (err) {
      setError('Failed to load sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = useCallback(async (search) => {
    try {
      const response = await adminAPI.getTrainingUsers(search);
      setUsers(response.data.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (createDialog) {
        loadUsers(userSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, createDialog, loadUsers]);

  const handleCreateSession = async () => {
    if (!createData.client_id || !createData.scenario_id) {
      setError('Please select a user and scenario');
      return;
    }

    setCreateLoading(true);
    setError('');

    try {
      const response = await adminAPI.createTrainingSession(createData);
      
      if (response.data.success) {
        setSuccess(
          response.data.sent 
            ? `Training sent to ${response.data.session.client_name}!`
            : `Training session created (not sent yet)`
        );
        setCreateDialog(false);
        setCreateData({
          client_id: '',
          scenario_id: '',
          channel: 'sms',
          send_immediately: true
        });
        setSelectedUser(null);
        loadStats();
        loadSessions();
      } else {
        setError(response.data.sendError || 'Failed to create session');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create training session');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSendSession = async (sessionId) => {
    try {
      await adminAPI.sendTrainingSession(sessionId);
      setSuccess('Training message sent!');
      loadSessions();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send training');
    }
  };

  const handleCancelSession = async (sessionId) => {
    try {
      await adminAPI.cancelTrainingSession(sessionId);
      setSuccess('Training session cancelled');
      loadSessions();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel session');
    }
  };

  const handleRefresh = () => {
    loadStats();
    loadSessions();
    loadScenarios();
  };

  // Calculate accuracy percentage
  const accuracy = stats && (parseInt(stats.correct_responses) + parseInt(stats.incorrect_responses)) > 0
    ? Math.round((parseInt(stats.correct_responses) / (parseInt(stats.correct_responses) + parseInt(stats.incorrect_responses))) * 100)
    : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Training</Typography>
          <Typography variant="body2" color="text.secondary">
            Send training scenarios to test scam identification skills
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setCreateDialog(true)}
          >
            Send Training
          </Button>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<TrainingIcon />}
              label="Total Sessions"
              value={stats.total_sessions}
              color="primary"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<SendIcon />}
              label="Sent"
              value={stats.sent}
              color="info"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<CorrectIcon />}
              label="Completed"
              value={stats.completed}
              color="success"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<CorrectIcon />}
              label="Correct"
              value={stats.correct_responses}
              color="success"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<WrongIcon />}
              label="Incorrect"
              value={stats.incorrect_responses}
              color="error"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <StatCard
              icon={<TrainingIcon />}
              label="Accuracy"
              value={`${accuracy}%`}
              color={accuracy >= 70 ? 'success' : accuracy >= 50 ? 'warning' : 'error'}
            />
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label="Sessions" />
          <Tab label="Scenarios" />
        </Tabs>

        {/* Sessions Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={sessionFilter.status}
                label="Status"
                onChange={(e) => setSessionFilter(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={sessionFilter.channel}
                label="Channel"
                onChange={(e) => setSessionFilter(prev => ({ ...prev, channel: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {sessionsTotal} total sessions
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Scenario</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Expected Risk</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Result</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Response Time</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {session.client_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.client_phone || session.client_email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{session.scenario_name || 'Random'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Tooltip title={session.session_type}>
                          <Box><ChannelIcon channel={session.session_type} /></Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell><StatusChip status={session.status} /></TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <RiskChip level={session.expected_risk_level} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {session.status === 'completed' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ResponseIcon
                              correct={session.response_correct}
                              identified={session.client_identified_as}
                            />
                            <Typography variant="caption">
                              {session.client_identified_as}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {session.response_time_seconds && (
                          <Typography variant="body2">
                            {session.response_time_seconds < 60
                              ? `${session.response_time_seconds}s`
                              : `${Math.round(session.response_time_seconds / 60)}m`
                            }
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="caption">
                          {format(new Date(session.created_at), 'MMM d, h:mm a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {session.status === 'pending' && (
                          <>
                            <Tooltip title="Send Now">
                              <IconButton 
                                size="small" 
                                onClick={() => handleSendSession(session.id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton 
                                size="small" 
                                onClick={() => handleCancelSession(session.id)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {session.status === 'sent' && (
                          <Tooltip title="Cancel">
                            <IconButton 
                              size="small" 
                              onClick={() => handleCancelSession(session.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No training sessions found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Scenarios Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Difficulty</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Description</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scenarios.map((scenario) => (
                  <TableRow key={scenario.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {scenario.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Chip size="small" label={scenario.scenario_type} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Tooltip title={scenario.channel}>
                        <Box><ChannelIcon channel={scenario.channel} /></Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <RiskChip level={scenario.expected_risk_level} />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip
                        size="small"
                        label={scenario.difficulty}
                        color={
                          scenario.difficulty === 'easy' ? 'success' :
                          scenario.difficulty === 'medium' ? 'warning' : 'error'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ maxWidth: { xs: 150, md: 300 } }} noWrap>
                        {scenario.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={scenario.is_active ? 'Active' : 'Inactive'}
                        color={scenario.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Create Training Dialog */}
      <Dialog 
        open={createDialog} 
        onClose={() => setCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Training</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => 
                `${option.name || 'Unnamed'} (${option.phone || option.email})`
              }
              value={selectedUser}
              onChange={(e, newValue) => {
                setSelectedUser(newValue);
                setCreateData(prev => ({ ...prev, client_id: newValue?.id || '' }));
              }}
              onInputChange={(e, value) => setUserSearch(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User"
                  placeholder="Search by name, email, or phone"
                  required
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.name || 'Unnamed'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.phone} · {option.email} · {option.training_count} trainings ({option.correct_count} correct)
                    </Typography>
                  </Box>
                </li>
              )}
            />

            <FormControl fullWidth>
              <InputLabel>Scenario</InputLabel>
              <Select
                value={createData.scenario_id}
                label="Scenario"
                onChange={(e) => setCreateData(prev => ({ ...prev, scenario_id: e.target.value }))}
              >
                <MenuItem value="">
                  <em>Random (system picks for {createData.channel})</em>
                </MenuItem>
                {(() => {
                  // Group scenarios by type, showing one entry per type
                  const filtered = scenarios.filter(s => s.is_active && s.channel === createData.channel);
                  const grouped = {};
                  filtered.forEach(s => {
                    if (!grouped[s.scenario_type]) grouped[s.scenario_type] = [];
                    grouped[s.scenario_type].push(s);
                  });
                  return Object.entries(grouped).map(([type, variants]) => {
                    const first = variants[0];
                    if (variants.length === 1) {
                      return (
                        <MenuItem key={first.id} value={first.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RiskChip level={first.expected_risk_level} />
                            {first.name}
                          </Box>
                        </MenuItem>
                      );
                    }
                    // Multiple variants — show group header + individual variants
                    return [
                      <ListSubheader key={`header-${type}`} sx={{ bgcolor: 'grey.50', lineHeight: '32px' }}>
                        {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} ({variants.length} variants)
                      </ListSubheader>,
                      ...variants.map(v => (
                        <MenuItem key={v.id} value={v.id} sx={{ pl: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RiskChip level={v.expected_risk_level} />
                            {v.name}
                          </Box>
                        </MenuItem>
                      ))
                    ];
                  });
                })()}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={createData.channel}
                label="Channel"
                onChange={(e) => setCreateData(prev => ({ 
                  ...prev, 
                  channel: e.target.value,
                  scenario_id: '' // Clear scenario when channel changes
                }))}
              >
                <MenuItem value="sms">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon fontSize="small" /> SMS
                  </Box>
                </MenuItem>
                <MenuItem value="email">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" /> Email
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {selectedUser && (
              <Alert severity="info">
                Training will be sent to: {' '}
                {createData.channel === 'sms' ? selectedUser.phone : selectedUser.email}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSession}
            disabled={createLoading || !createData.client_id}
            startIcon={createLoading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Send Training
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Training;